import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'What does an AI file organizer for lawyers actually do?',
    answer:
      "It reads each file on your Mac (PDFs, Word docs, scans, screenshots, audio), extracts the client name, opposing party, matter ID, document type, and dates, then routes the file to the right client matter folder and renames it consistently. Sortio reads files on your Mac, and classification runs against the model you choose. In Offline mode (Ollama) or with BYOK that inference stays on your machine (with BYOK it goes to your own provider). By default Sortio uses our cloud (OpenAI), sending filenames and extracted entities for matching, with file contents sent only if you enable a content-based feature. When you turn on the Clio integration, approved file uploads go from your Mac directly to Clio.",
  },
  {
    question: 'How is this different from your existing law firm page?',
    answer:
      "The for-law-firms page focuses on solo attorneys and small firms already using Clio. This lawyers page is broader. It covers the underlying file-organization problem for any lawyer, in-house counsel, court-appointed attorney, or mid-size firm that may or may not be on Clio yet. Same product, different entry point.",
  },
  {
    question: 'Does Sortio file documents into the right Clio matter automatically?',
    answer:
      "Yes. With the Clio Manage integration connected, Sortio pulls your live matter list through the official API. For each file, it extracts the entities (client, opposing party, matter ID, case caption) and matches them against your matters. A confidence score is shown for every assignment and you approve the batch before anything uploads. Low-confidence files are held in a review queue.",
  },
  {
    question: 'How does Sortio handle matter-based filing for a firm that does not use Clio?',
    answer:
      "Same workflow, local destination instead of Clio. Point Sortio at your existing matter folder structure (often something like Clients/Smith/00012-Personal-Injury) and it routes new intake into the right matter without touching your naming convention. The Rule Builder lets you enforce sub-foldering (correspondence, pleadings, discovery, billing) so every matter ends up with the same internal layout.",
  },
  {
    question: 'Will privileged or work product documents ever leave my Mac?',
    answer:
      "Not unless you tell Sortio to upload them. Classification runs locally against the LLM you choose. In Ollama mode no document content (and no matter party names) ever cross the network. In BYOK mode requests go from your Mac directly to your provider on your account. With the default Sortio classifier, file content stays local and only filename plus extracted entities are sent for matter matching, redacted after 30 days per our privacy policy.",
  },
  {
    question: 'Can Sortio handle multi-attorney workflows with overlapping matters?',
    answer:
      "Yes. Each attorney can run their own Sortio install pointed at the same shared drive, with a shared rule set so naming and routing stay consistent across the firm. Team plans add a centralized rule library and audit log. Conflicting matter assignments (two attorneys flagged the same file to different matters) surface in the next sync rather than getting silently overwritten.",
  },
  {
    question: 'Does Sortio enforce retention rules for closed matters?',
    answer:
      "Sortio does not delete files for you (a deliberate choice). What it does do: tag every file with a closing date and a state retention class when a matter closes, pack the matter into a read-only archive, and surface the archive for deletion review when the retention window expires. The audit log captures the chain of custody from intake through retention. Your firm makes the final delete decision, but Sortio gives you the inventory.",
  },
  {
    question: 'I have a folder of mixed scans from twenty different clients. Can Sortio split them up?',
    answer:
      "Yes. Drop the folder into Sortio and the AI identifies the client, opposing party, and document type on each file independently. scan_001.pdf through scan_400.pdf, interleaved across twenty different clients, all sort correctly. The Rule Builder lets you say something like \"if a file mentions a client we already have a folder for, file it there; otherwise create a new client folder by last name and stage the file for intake review.\"",
  },
  {
    question: 'How does Sortio support contract management workflows?',
    answer:
      "It treats contracts as a document class. Engagement letters, NDAs, fee agreements, settlement agreements, and amendments are tagged as contracts and routed to the relevant matter or a top-level Contracts folder, depending on your rule. Sortio reads each contract for the parties, effective date, and any amendment-of reference so you can sort by execution date or by counterparty inside the matter folder.",
  },
  {
    question: 'What about invoices, expert reports, and other vendor documents?',
    answer:
      "Vendor invoices (expert witnesses, court reporters, filing services, e-discovery vendors) are recognized as billing documents and filed separately from substantive case material. They get a clean filename pattern like 2026-04-12 - Vendor - Matter - $1,250.pdf. For a deeper dive on the same pattern, see our guide on automatically renaming PDFs by content.",
  },
  {
    question: 'Does Sortio support Bates numbering, redaction, or e-filing?',
    answer:
      "No. Sortio classifies, names, and files. It does not stamp, redact, or e-file. It runs cleanly alongside Adobe Acrobat, Foxit, and PDF Expert for those workflows. The typical sequence is: Sortio organizes the production set, you stamp and redact in your PDF tool, then Sortio re-files the stamped versions into the matter.",
  },
  {
    question: 'How fast is it on a real intake backlog of a few thousand documents?',
    answer:
      "AI sort runs up to 5,000 files per pass on Pro and completes a typical 1,000-file intake in a few minutes on a modern Mac. For larger archives (a 30,000-file legacy matter), use the Rule Builder. Rule-based sorts process unlimited files instantly and never touch your AI allowance. See our AI Sort vs Rule Builder guide for when to pick each.",
  },
  {
    question: 'Does it work on Windows or only Mac?',
    answer:
      "Sortio ships for macOS, Windows, and Linux today. Macs are the most common deployment we see in law practices, but the same app runs on Windows for firms standardized there. The Clio integration works identically on both platforms.",
  },
  {
    question: 'What does it cost for a solo attorney to start?',
    answer:
      "The free tier gives you a one-time AI trial allowance so you can try it on a real intake folder. Pro is $14.99 per month or $99 per year, including Clio integration, a monthly AI allowance sized for heavy use, AI sort up to 5,000 files per run, auto-sort on file change, and BYOK so you can plug in your own provider key. Team is $29 per seat per month for multi-attorney firms that want a shared rule library and centralized AI policy.",
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
    <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
      <button
        className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
          {isOpen ? (
            <ChevronUp className="w-3.5 h-3.5 text-sortio-primary transition-transform duration-300" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-apple-300 transition-transform duration-300" />
          )}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0">
          <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  </div>
);

const LawyersFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section id="lawyers-faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50 text-center">
          Lawyer <span className="apple-gradient-text">FAQ</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl text-center">
          Questions from solo attorneys, mid-size litigation shops, and in-house counsel weighing
          Sortio for matter-based filing, retention, and Clio integration.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {FAQS.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
          />
        ))}
      </div>
    </section>
  );
};

export default LawyersFAQ;
